import React, { useState, useEffect } from "react";

// Componente de celebración con confetti y efectos
function CelebrationEffect({ show }: { show: boolean }) {
  const [particles, setParticles] = useState<Array<{ id: number; left: number; delay: number; color: string; size: number; type: 'confetti' | 'star' | 'sparkle' }>>([]);
  
  useEffect(() => {
    if (show) {
      const colors = ['#ffd700', '#ff6b6b', '#4ade80', '#60a5fa', '#f472b6', '#a78bfa', '#fb923c', '#22d3d8'];
      const newParticles = Array.from({ length: 50 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 2,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: Math.random() * 10 + 5,
        type: (['confetti', 'star', 'sparkle'] as const)[Math.floor(Math.random() * 3)]
      }));
      setParticles(newParticles);
    }
  }, [show]);

  if (!show) return null;

  return (
    <div className="celebration-container">
      {particles.map((p) => (
        <div
          key={p.id}
          className={`celebration-particle ${p.type}`}
          style={{
            left: `${p.left}%`,
            animationDelay: `${p.delay}s`,
            backgroundColor: p.type === 'confetti' ? p.color : 'transparent',
            width: p.size,
            height: p.type === 'confetti' ? p.size * 0.6 : p.size,
            color: p.color,
          }}
        >
          {p.type === 'star' && '★'}
          {p.type === 'sparkle' && '✦'}
        </div>
      ))}
      <div className="celebration-glow"></div>
    </div>
  );
}

// Tipos expandidos para manejar variaciones en el JSON
type NumericLike = number | string | null | undefined;

type AlexiaExam = {
  asignatura?: string;
  nota?: NumericLike;
  media_companeros?: NumericLike;
  evaluacion?: string;
  external_course_key_bb?: string;
  fecha?: string;
  nota_visual?: string;
  [key: string]: any;
};

const toNumber = (value: NumericLike, fallback = 0) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : fallback;
  }

  if (typeof value === "string") {
    const normalized = value.replace(",", ".");
    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  return fallback;
};

interface DashboardData {
  tareas_totales?: number;
  porcentaje_tareas_alumno?: number;
  tareas_entregadas_pct?: number;
  porcentaje_tareas_clase?: number;
  nota_examen?: number;
  nota?: number;
  nota_maxima?: number;
  nota_media_clase?: number;
  media_clase?: number;
  tareas_pendientes?: Array<{ nombre?: string; fecha_limite?: string; asignatura?: string }>;
  tareas_vencidas?: Array<{ nombre?: string; fecha_limite?: string; asignatura?: string }>;
  pendientes_semana?: Array<{ nombre?: string; fecha_limite?: string; asignatura?: string }>;
  pendientes_mes?: Array<{ nombre?: string; fecha_limite?: string; asignatura?: string }>;
  examenes?: Array<{
    nombre_examen?: string;
    nombre?: string;
    nombre_asignatura?: string;
    nota_examen?: number;
    nota?: number;
    nota_obtenida_alumno?: number | null;
    nota_maxima?: number;
    nota_max_alumno?: number;
    media_clase?: number;
    nota_media_clase?: number;
    nota_obtenida_clase?: number;
    nota_max_clase?: number;
    nota_visual?: string;
    score?: number;
    max?: number;
  }>;
  calificaciones_tareas?: Array<{
    asignatura?: string;
    media_clase?: NumericLike;
    nombre_tarea?: string;
    nota_max?: NumericLike;
    nota_obtenida?: NumericLike;
  }>;
  examenes_alexia?: AlexiaExam[];
  examenes_primera_ev?: AlexiaExam[];
  notas_primera_ev?: AlexiaExam[];
  examenesPrimeraEv?: AlexiaExam[];
  examenesPrimera?: AlexiaExam[];
  nota_media_alumno?: number;
  nota_media_companeros?: number;
  [key: string]: any;  // Para propiedades extras
}

// Helper para obtener nota numérica o null si pendiente/inválida
const getNota = (exam: any): number | null => {
  const rawNota = exam.nota_obtenida_alumno ?? exam.nota ?? exam.nota_examen ?? exam.score;
  if (typeof exam.nota_visual === "string" && exam.nota_visual.toLowerCase() === "pendiente") {
    return null;
  }
  if (rawNota === null || rawNota === undefined) {
    return null;
  }
  const num = toNumber(rawNota);
  if (!Number.isFinite(num)) {
    return null;
  }
  return num;
};

const averageNumericField = (
  entries: AlexiaExam[],
  extractor: (exam: AlexiaExam) => NumericLike
): number | null => {
  let sum = 0;
  let count = 0;

  entries.forEach((entry) => {
    const rawValue = extractor(entry);
    const numericValue = toNumber(rawValue, NaN);

    if (Number.isFinite(numericValue)) {
      sum += numericValue;
      count += 1;
    }
  });

  return count > 0 ? sum / count : null;
};

// Componente SVG de progreso circular (ajustado para mejor manejo de texto largo)
function CircularProgress({
  value,
  max = 100,
  size = 140,
  strokeWidth = 12,
  color = "#3b82f6",
  text,
}: {
  value: number;
  max?: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
  text: string;
}) {
  const percentage = (value / max) * 100;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;

  // Font size ajustado: más pequeño para tamaños menores y texto largo
  let fontSize = Math.max(10, Math.min(24, Math.round(size * 0.15)));
  if (text.length > 10) { // e.g., "10.00 / 10.00" tiene 13 chars
    fontSize *= 0.8; // Reducir un 20% si texto es largo
  }

  return (
    <div className="circular-progress" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle
          className="circular-bg"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
        />
        <circle
          className="circular-bar"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          stroke={color}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <div 
        className="circular-text" 
        style={{ 
          color, 
          fontSize: `${fontSize}px`,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: '90%'
        }}
      >
        {text}
      </div>
    </div>
  );
}

// Nuevo componente para gráfico de barras horizontales
function BarChart({
  studentValue,
  classValue,
  max,
  isPending,
  color,
  studentLabel,
  classLabel,
}: {
  studentValue: NumericLike;
  classValue: NumericLike;
  max: NumericLike;
  isPending: boolean;
  color: string;
  studentLabel?: string;
  classLabel?: string;
}) {
  if (isPending) {
    return (
      <div className="pending-placeholder">
        <p className="pending-text">Pendiente</p>
        <p className="exam-media">
          {classLabel ?? "Media compañeros: —"}
        </p>
      </div>
    );
  }

  const safeMax = Math.max(toNumber(max, 1), 1); // evita dividir por 0
  const safeStudent = toNumber(studentValue);
  const safeClass = toNumber(classValue);

  const studentPct = (safeStudent / safeMax) * 100;
  const classPct = (safeClass / safeMax) * 100;

  const studentDisplayLabel =
    studentLabel ?? `Tú: ${safeStudent.toFixed(1)}/${safeMax.toFixed(0)}`;
  const classDisplayLabel =
    classLabel ?? `Clase: ${safeClass.toFixed(1)}/${safeMax.toFixed(0)}`;

  return (
    <div className="bar-chart">
      <div className="bar-group">
        <span>{studentDisplayLabel}</span>
        <div className="bar-container">
          <div
            className="bar student"
            style={{ width: `${studentPct}%`, backgroundColor: color }}
          ></div>
        </div>
      </div>
      <div className="bar-group">
        <span>{classDisplayLabel}</span>
        <div className="bar-container">
          <div
            className="bar class"
            style={{ width: `${classPct}%`, backgroundColor: "#6b7280" }}
          ></div>
        </div>
      </div>
    </div>
  );
}

const parseAndFormatDate = (dateString: string | undefined) => {
  if (!dateString) return "Sin fecha";

  // Intenta parsear diferentes formatos comunes
  let date: Date | null = null;

  // Nuevo: Formato DD/MM/YYYY [HH:MM:SS] (opcional el tiempo)
  let match = dateString.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?: (\d{2}):(\d{2}):(\d{2}))?$/);
  if (match) {
    const [, day, month, year, hour = '00', minute = '00', second = '00'] = match;
    const paddedDay = day.padStart(2, '0');
    const paddedMonth = month.padStart(2, '0');
    date = new Date(`${year}-${paddedMonth}-${paddedDay}T${hour}:${minute}:${second}`);
  }

  // Formato original: DD-MM-YYYY HH:MM:SS
  if (!date || isNaN(date.getTime())) {
    match = dateString.match(/^(\d{2})-(\d{2})-(\d{4}) (\d{2}):(\d{2}):(\d{2})$/);
    if (match) {
      const [, day, month, year, hour, minute, second] = match;
      date = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
    }
  }

  // Formato YYYY-MM-DD HH:MM:SS
  if (!date || isNaN(date.getTime())) {
    match = dateString.match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/);
    if (match) {
      const [, year, month, day, hour, minute, second] = match;
      date = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
    }
  }

  // Si falla, intenta ISO estándar sin segundos o con Z
  if (!date || isNaN(date.getTime())) {
    date = new Date(dateString);
  }

  if (isNaN(date.getTime())) return "Fecha inválida";

  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const isTomorrow = date.toDateString() === tomorrow.toDateString();

  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  };

  if (isToday) return `Hoy, ${date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`;
  if (isTomorrow) return `Mañana, ${date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`;

  return date.toLocaleDateString('es-ES', options);
};

const Dashboard: React.FC<{ data: DashboardData }> = ({ data }) => {
  // Manejo robusto de datos con fallbacks
  const pctTareasAlumno = toNumber(
    data.pct_tareas_alumno ??
    data.porcentaje_tareas_alumno ??
    0
  );

  const pctTareasClase = toNumber(
    data.pct_tareas_clase ??
    data.porcentaje_tareas_clase ??
    0
  );

  const pendientesList = Array.isArray(data.pendientes_semana) ? data.pendientes_semana :
                         Array.isArray(data.pendientes_mes) ? data.pendientes_mes :
                         Array.isArray(data.tareas_pendientes) ? data.tareas_pendientes : [];
  const vencidasList = Array.isArray(data.tareas_vencidas) ? data.tareas_vencidas : [];
  const tareasPendientes = pendientesList.length;
  const tareasVencidas = vencidasList.length;
  const tareasTotales = toNumber(data.tareas_totales ?? 0);

  const entregasAlumno = toNumber(data.entregas_alumno ?? Math.round((pctTareasAlumno / 100) * tareasTotales));
  const mediaEntregasClase = toNumber(data.media_entregas_clase ?? ((pctTareasClase / 100) * tareasTotales));

  // Estados para desplegables
  const [showPendientes, setShowPendientes] = useState(false);
  const [showVencidas, setShowVencidas] = useState(false);
  const [showExams, setShowExams] = useState(true);
  const [showTasks, setShowTasks] = useState(true);
  const [showAlexia, setShowAlexia] = useState(true);

  // Procesamiento de exámenes
  const exams =
    (data.examenes && Array.isArray(data.examenes) && data.examenes) ||
    (data.nota_examenes && Array.isArray(data.nota_examenes) && data.nota_examenes) ||
    [];

  // Procesamiento de exámenes Alexia
  const alexiaCandidates = [
    data.examenes_alexia,
    data.notas_primera_ev,
    data.examenes_primera_ev,
    (data as any).examenesPrimeraEv,
    (data as any)["examenesPrimera ev"],
    (data as any).examenesPrimera,
  ];
  const alexiaSource = alexiaCandidates.find((candidate) => Array.isArray(candidate));
  const examenesAlexia: AlexiaExam[] = Array.isArray(alexiaSource) ? (alexiaSource as AlexiaExam[]) : [];
  const directAlexiaStudentAverage = toNumber(data.nota_media_alumno, NaN);
  const directAlexiaClassAverage = toNumber(data.nota_media_companeros, NaN);
  const alexiaStudentAverage =
    Number.isFinite(directAlexiaStudentAverage)
      ? directAlexiaStudentAverage
      : averageNumericField(
          examenesAlexia,
          (exam) =>
            exam.nota ??
            exam.nota_obtenida_alumno ??
            exam.nota_examen ??
            exam.score ??
            NaN
        );
  const alexiaClassAverage =
    Number.isFinite(directAlexiaClassAverage)
      ? directAlexiaClassAverage
      : averageNumericField(
          examenesAlexia,
          (exam) => exam.media_companeros ?? exam.media ?? exam.nota_media_clase ?? NaN
        );
  const alexiaHasAverage = alexiaStudentAverage !== null || alexiaClassAverage !== null;

  // Procesamiento de calificaciones de tareas
  const calificaciones = Array.isArray(data.calificaciones_tareas) ? data.calificaciones_tareas : [];

  // Detecta si TODOS los exámenes están pendientes
  const allPending = exams.length > 0 && exams.every((exam) => getNota(exam) === null);

  // Preparar valores para la tarjeta principal (displayNota, displayMax, displayMedia)
  let displayNota = 0;
  let displayMax = 10;
  let displayMedia = 0;
  let hasExams = exams.length > 0; // true si hay al menos un examen (aunque estén pendientes)

  const validExams = exams.filter((exam) => getNota(exam) !== null);
  const count = validExams.length;

  if (count > 0) {
    if (count === 1) {
      // Para un solo examen, mostrar valores reales sin normalizar
      const exam = validExams[0];
      displayNota = getNota(exam)!;
      displayMax = toNumber(
        exam.nota_max_alumno ??
        exam.nota_maxima ??
        exam.notaMaxima ??
        exam.max ?? 10
      );
      displayMedia = toNumber(
        exam.nota_obtenida_clase ??
        exam.media_clase ??
        exam.nota_media_clase ??
        exam.media ?? 0
      );
    } else {
      // Para múltiples, normalizar y promediar
      let sumNotaNorm = 0;
      let sumMediaNorm = 0;

      validExams.forEach((exam) => {
        const nota = getNota(exam)!;

        const maxA = toNumber(
          exam.nota_max_alumno ??
          exam.nota_maxima ??
          exam.notaMaxima ??
          exam.max ?? 10
        );

        const media = toNumber(
          exam.nota_obtenida_clase ??
          exam.media_clase ??
          exam.nota_media_clase ??
          exam.media ?? 0
        );

        const maxC = toNumber(
          exam.nota_max_clase ??
          exam.nota_maxima ??
          exam.notaMaxima ??
          exam.max ?? maxA
        );

        sumNotaNorm += maxA > 0 ? (nota / maxA) * 10 : 0;
        sumMediaNorm += maxC > 0 ? (media / maxC) * 10 : 0;
      });

      displayNota = sumNotaNorm / count;
      displayMedia = sumMediaNorm / count;
      displayMax = 10;
    }
  }

  // Si todos están pendientes, no consideramos que haya "nota" numérica a mostrar
  const notaPct = displayMax > 0 ? (displayNota / displayMax) * 100 : 0;
  const colorNota =
    notaPct >= 70 ? "#10b981" : notaPct >= 50 ? "#f59e0b" : "#ef4444";

  // Colores condicionales para entregas
  const aboveClassEntrega = pctTareasAlumno > pctTareasClase;
  const colorEntrega =
    aboveClassEntrega
      ? "#10b981"
      : pctTareasAlumno >= 80
      ? "#10b981"
      : pctTareasAlumno >= 60
      ? "#f59e0b"
      : "#ef4444";

  const getAlexiaColor = (value: number) =>
    value >= 7 ? "#10b981" : value >= 5 ? "#f59e0b" : "#ef4444";

  // Debug: Loggea todos los datos para ver qué llega
  console.log("📊 Datos crudos recibidos: ", data);
  console.log("📊 Datos procesados:", {
    pctTareasAlumno,
    pctTareasClase,
    displayNota,
    displayMax,
    displayMedia,
    notaPct,
    tareasPendientes,
    tareasVencidas,
    tareasTotales,
    examsCount: exams.length,
    allPending,
  });

  return (
    <div className="dashboard-container">
      <h1 className="dashboard-title">Mi Rendimiento Académico</h1>

      <div className="dashboard-grid">
        {/* Tarjeta 1: Entregas */}
        <div className="dashboard-card">
          <h2 className="card-title">Entrega de Tareas</h2>
          <div className="progress-dual">
            <div>
              <CircularProgress
                value={pctTareasAlumno}
                text={`${entregasAlumno}/${tareasTotales}`}
                color={colorEntrega}
              />
              <p className="progress-label">Tú</p>
            </div>
            <div>
              <CircularProgress
                value={pctTareasClase}
                text={`${mediaEntregasClase.toFixed(1)}/${tareasTotales}`}
                color="#6b7280"
              />
              <p className="progress-label">Clase</p>
            </div>
          </div>
          
          {/* Barras de porcentaje comparativas */}
          <div className="tareas-comparison">
            <div className="tareas-comparison-row">
              <span className="tareas-comparison-label">Tú</span>
              <div className="tareas-comparison-bar-container">
                <div 
                  className="tareas-comparison-bar" 
                  style={{ 
                    width: `${pctTareasAlumno}%`,
                    backgroundColor: colorEntrega
                  }}
                ></div>
              </div>
              <span 
                className="tareas-comparison-value"
                style={{ color: colorEntrega }}
              >
                {pctTareasAlumno.toFixed(0)}%
              </span>
            </div>
            <div className="tareas-comparison-row">
              <span className="tareas-comparison-label">Clase</span>
              <div className="tareas-comparison-bar-container">
                <div 
                  className="tareas-comparison-bar" 
                  style={{ 
                    width: `${pctTareasClase}%`,
                    backgroundColor: '#6b7280'
                  }}
                ></div>
              </div>
              <span className="tareas-comparison-value" style={{ color: '#6b7280' }}>
                {pctTareasClase.toFixed(0)}%
              </span>
            </div>
          </div>

          <p className="total-tareas">
            Total: <strong>{tareasTotales}</strong> tareas
          </p>
          {pctTareasAlumno === 0 && <p className="fallback-text">No hay datos de entregas disponibles</p>}
        </div>

        {/* Tarjeta 2: Notas medias - BlackBoard y Alexia */}
        <div className="dashboard-card notas-card">
          <h2 className="card-title">Notas Medias</h2>

          {/* Sección BlackBoard */}
          <div className="notas-section">
            <h3 className="notas-section-title blackboard-title">📚 BlackBoard</h3>
            {hasExams && !allPending ? (
              <div className="notas-comparison">
                <div className="notas-comparison-row">
                  <span className="notas-comparison-label">Tú</span>
                  <div className="notas-comparison-bar-container">
                    <div 
                      className="notas-comparison-bar" 
                      style={{ 
                        width: `${(displayNota / displayMax) * 100}%`,
                        backgroundColor: colorNota
                      }}
                    ></div>
                  </div>
                  <span className="notas-comparison-value" style={{ color: colorNota }}>
                    {displayNota.toFixed(2)}
                  </span>
                </div>
                <div className="notas-comparison-row">
                  <span className="notas-comparison-label">Clase</span>
                  <div className="notas-comparison-bar-container">
                    <div 
                      className="notas-comparison-bar" 
                      style={{ 
                        width: `${(displayMedia / displayMax) * 100}%`,
                        backgroundColor: '#6b7280'
                      }}
                    ></div>
                  </div>
                  <span className="notas-comparison-value" style={{ color: '#6b7280' }}>
                    {displayMedia.toFixed(2)}
                  </span>
                </div>
                <p className="notas-count">
                  {count} {count === 1 ? 'examen' : 'exámenes'}
                </p>
              </div>
            ) : (
              <p className="notas-no-data">
                {allPending ? 'Exámenes pendientes' : 'Sin exámenes'}
              </p>
            )}
          </div>

          {/* Sección Alexia */}
          <div className="notas-section alexia-section">
            <h3 className="notas-section-title alexia-title">🎓 Alexia</h3>
            {alexiaHasAverage ? (
              <div className="notas-comparison">
                {alexiaStudentAverage !== null && (
                  <div className="notas-comparison-row">
                    <span className="notas-comparison-label">Tú</span>
                    <div className="notas-comparison-bar-container">
                      <div
                        className="notas-comparison-bar"
                        style={{
                          width: `${(alexiaStudentAverage / 10) * 100}%`,
                          backgroundColor: getAlexiaColor(alexiaStudentAverage),
                        }}
                      ></div>
                    </div>
                    <span
                      className="notas-comparison-value"
                      style={{ color: getAlexiaColor(alexiaStudentAverage) }}
                    >
                      {alexiaStudentAverage.toFixed(2)}
                    </span>
                  </div>
                )}
                {alexiaClassAverage !== null && (
                  <div className="notas-comparison-row">
                    <span className="notas-comparison-label">Clase</span>
                    <div className="notas-comparison-bar-container">
                      <div
                        className="notas-comparison-bar"
                        style={{
                          width: `${(alexiaClassAverage / 10) * 100}%`,
                          backgroundColor: "#6b7280",
                        }}
                      ></div>
                    </div>
                    <span className="notas-comparison-value" style={{ color: "#6b7280" }}>
                      {alexiaClassAverage.toFixed(2)}
                    </span>
                  </div>
                )}
                <p className="notas-count">
                  {examenesAlexia.length} {examenesAlexia.length === 1 ? "registro" : "registros"}
                </p>
                {examenesAlexia.length > 0 && (
                  <p className="alexia-meta">
                    Medias derivadas de {examenesAlexia.length}{" "}
                    {examenesAlexia.length === 1 ? "registro" : "registros"}.
                  </p>
                )}
              </div>
            ) : (
              <p className="notas-no-data">
                {examenesAlexia.length > 0
                  ? "Medias derivadas de los registros que aparecen abajo."
                  : "Sin datos de Alexia"}
              </p>
            )}
          </div>
        </div>

        {/* Tarjeta 3: Tareas pendientes */}
        <div className="dashboard-card">
          <h2 className="card-title">Tareas Actuales</h2>
          
          <div className="pending-summary">
            <div>
              <span>Pendientes esta semana</span>
              <span className={tareasPendientes === 0 ? "ok" : "warning"}>
                {tareasPendientes}
              </span>
            </div>
            <div>
              <span>Vencidas últimos 14 días</span>
              <span className={tareasVencidas === 0 ? "ok" : "danger"}>
                {tareasVencidas}
              </span>
            </div>
          </div>

          {/* Desplegable para tareas pendientes */}
          {tareasPendientes > 0 && (
            <>
              <button 
                className="toggle-button" 
                onClick={() => setShowPendientes(!showPendientes)}
                aria-expanded={showPendientes}
              >
                {showPendientes ? 'Ocultar' : 'Ver'} Tareas Pendientes
                <span className="toggle-icon">{showPendientes ? '▲' : '▼'}</span>
              </button>
              {showPendientes && (
                <div className="tareas-list">
                  {pendientesList.map((tarea, index) => (
                    <div key={index} className="tarea-item" lang="es">
                      <div className="tarea-header">
                        <strong className="tarea-nombre">{tarea.nombre || "Sin nombre"}</strong>
                        <span className="tarea-fecha">
                          {parseAndFormatDate(tarea.fecha_limite)}
                        </span>
                      </div>
                      <div className="tarea-asignatura">
                        {tarea.asignatura || "Asignatura no especificada"}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Desplegable para tareas vencidas */}
          {tareasVencidas > 0 && (
            <>
              <button 
                className="toggle-button-vencidas" 
                onClick={() => setShowVencidas(!showVencidas)}
                aria-expanded={showVencidas}
              >
                {showVencidas ? 'Ocultar' : 'Ver'} Tareas Vencidas últimos 15 días
                <span className="toggle-icon">{showVencidas ? '▲' : '▼'}</span>
              </button>
              {showVencidas && (
                <div className="tareas-list vencidas">
                  {vencidasList.map((tarea, index) => (
                    <div key={index} className="tarea-item vencida" lang="es">
                      <div className="tarea-header">
                        <strong className="tarea-nombre">{tarea.nombre || "Sin nombre"}</strong>
                        <span className="tarea-fecha vencida">
                          Venció el {parseAndFormatDate(tarea.fecha_limite)}
                        </span>
                      </div>
                      <div className="tarea-asignatura">
                        {tarea.asignatura || "Asignatura no especificada"}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Mensajes de estado */}
          {tareasPendientes === 0 && tareasVencidas === 0 && tareasTotales > 0 && (
            <div className="status-message all-done">
              ¡TODO AL DÍA!
            </div>
          )}

          {tareasPendientes > 0 && tareasVencidas === 0 && (
            <div className="status-message pending-only">
              Tienes {tareasPendientes} tarea{tareasPendientes > 1 ? "s" : ""} pendiente{tareasPendientes > 1 ? "s" : ""}
            </div>
          )}

          {tareasVencidas > 0 && (
            <div className="status-message overdue">
              ¡Urgente! Tienes {tareasVencidas} tarea{tareasVencidas > 1 ? "s" : ""} vencida{tareasVencidas > 1 ? "s" : ""}
            </div>
          )}

          {tareasTotales === 0 && (
            <p className="fallback-text">No hay tareas registradas aún</p>
          )}
        </div>
      </div>

      {/* Sección nueva: Lista de exámenes si hay array */}
      {exams.length > 0 && (
        <div className="exams-container">
          <h2 className="exams-title">Detalles de Exámenes BlackBoard</h2>
          <button 
            className="toggle-button" 
            onClick={() => setShowExams(!showExams)}
            aria-expanded={showExams}
          >
            {showExams ? 'Ocultar' : 'Ver'} Detalles
            <span className="toggle-icon">{showExams ? '▲' : '▼'}</span>
          </button>
          {showExams && (
            <div className="exams-grid">
              {exams.map((exam, index) => {
                // Detectar si el examen está pendiente
                const rawNota =
                  exam.nota_obtenida_alumno ??
                  exam.nota ??
                  exam.nota_examen ??
                  exam.score;

                const isPending =
                  (typeof exam.nota_visual === "string" && exam.nota_visual.toLowerCase() === "pendiente") ||
                  rawNota === null ||
                  rawNota === undefined ||
                  (typeof rawNota === 'string' && isNaN(parseFloat(rawNota)));

                // Si está pendiente, usar 0 para calcular la barra pero mostrar "Pendiente"
                const nota = isPending ? 0 : toNumber(rawNota ?? 0);

                const maxA =
                  toNumber(
                    exam.nota_max_alumno ??
                    exam.nota_maxima ??
                    exam.notaMaxima ??
                    exam.max ??
                    10
                  );

                const media =
                  toNumber(
                    exam.nota_obtenida_clase ??
                    exam.media_clase ??
                    exam.nota_media_clase ??
                    exam.media ??
                    0
                  );

                // Porcentaje (solo si hay nota)
                const pct = maxA > 0 ? (nota / maxA) * 100 : 0;

                // Color: si está pendiente, gris
                const color =
                  isPending
                    ? "#6b7280"
                    : pct >= 70
                    ? "#10b981"
                    : pct >= 50
                    ? "#f59e0b"
                    : "#ef4444";

                return (
                  <div key={index} className="exam-card">
                    <h3
                      className="exam-name"
                      style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
                    >
                      {exam.nombre ?? exam.nombre_examen ?? "Examen sin nombre"}
                    </h3>
                    <p
                      className="exam-subject"
                      style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
                    >
                      {exam.nombre_asignatura ?? "Asignatura no especificada"}
                    </p>
                    <BarChart
                      studentValue={nota}
                      classValue={media}
                      max={maxA}
                      isPending={isPending}
                      color={color}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Sección nueva: Exámenes Alexia */}
      {examenesAlexia.length > 0 && (
        <div className="exams-container">
          <h2 className="exams-title">Detalles de Exámenes Alexia</h2>
          <button 
            className="toggle-button alexia-toggle" 
            onClick={() => setShowAlexia(!showAlexia)}
            aria-expanded={showAlexia}
          >
            {showAlexia ? 'Ocultar' : 'Ver'} Detalles
            <span className="toggle-icon">{showAlexia ? '▲' : '▼'}</span>
          </button>
          {showAlexia && (
            <div className="exams-grid">
              {examenesAlexia.map((exam, index) => {
                const rawNota = exam.nota;
                const notaIsPending =
                  typeof rawNota === "string" && rawNota.toLowerCase() === "pendiente";
                const numericNota = notaIsPending ? 0 : toNumber(rawNota ?? 0);
                const maxNota = Math.max(
                  1,
                  toNumber(exam.nota_max_alumno ?? exam.nota_max ?? 10)
                );
                const notaColor = notaIsPending ? "#6b7280" : getAlexiaColor(numericNota);
                const mediaCompaneros = toNumber(
                  exam.media_companeros ?? exam.media ?? exam.nota_media_clase ?? NaN
                );
                const fechaLabel = exam.fecha ? parseAndFormatDate(exam.fecha) : null;

                const classValueForChart = Number.isFinite(mediaCompaneros)
                  ? mediaCompaneros
                  : 0;
                const chartMax = Math.max(maxNota, classValueForChart, 1);
                const classLabel =
                  Number.isFinite(mediaCompaneros)
                    ? `Clase: ${mediaCompaneros.toFixed(1)}/${chartMax.toFixed(0)}`
                    : "Clase: —";

                return (
                  <div key={index} className="exam-card alexia-card">
                    <div className="alexia-card-header">
                      <h3
                        className="exam-name"
                        style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
                      >
                        {exam.asignatura ?? "Asignatura sin nombre"}
                      </h3>
                      <p
                        className="exam-subject"
                        style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
                      >
                        {exam.evaluacion ?? "Evaluación no especificada"}
                      </p>
                    </div>
                    <BarChart
                      studentValue={numericNota}
                      classValue={classValueForChart}
                      max={chartMax}
                      isPending={notaIsPending}
                      color={notaColor}
                      classLabel={classLabel}
                    />
                    <div className="alexia-meta">
                      {fechaLabel && (
                        <div className="alexia-meta-row">
                          <span>Fecha</span>
                          <strong>{fechaLabel}</strong>
                        </div>
                      )}
                      {notaIsPending && exam.nota_visual && (
                        <div className="alexia-meta-row">
                          <span>Estado</span>
                          <strong>{exam.nota_visual}</strong>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Sección nueva: Lista de calificaciones de tareas si hay array */}
      {calificaciones.length > 0 && (
        <div className="exams-container"> {/* Reutilizando estilos de exams-container para consistencia */}
          <h2 className="exams-title">Detalles de Tareas</h2> {/* Reutilizando clase */}
          <button 
            className="toggle-button" 
            onClick={() => setShowTasks(!showTasks)}
            aria-expanded={showTasks}
          >
            {showTasks ? 'Ocultar' : 'Ver'} Detalles
            <span className="toggle-icon">{showTasks ? '▲' : '▼'}</span>
          </button>
          {showTasks && (
            <div className="exams-grid"> {/* Reutilizando clase */}
              {calificaciones.map((task, index) => {
                const nota = toNumber(task.nota_obtenida ?? 0);
                const maxN = toNumber(task.nota_max ?? 10);
                const media = toNumber(task.media_clase ?? 0);

                // Asumimos que todas las tareas tienen notas, pero agregamos check por si acaso
                const isPending = nota === 0 && media === 0; // O ajusta según tus datos

                const pct = maxN > 0 ? (nota / maxN) * 100 : 0;

                const hasValidMedia = Number.isFinite(media);
                const aboveClassAverage = hasValidMedia && nota > media;

                const color = isPending
                  ? "#6b7280"
                  : aboveClassAverage
                  ? "#10b981"
                  : pct >= 70
                  ? "#10b981"
                  : pct >= 50
                  ? "#f59e0b"
                  : "#ef4444";

                return (
                  <div key={index} className="exam-card"> {/* Reutilizando clase */}
                    <h3 className="exam-name">
                      {task.nombre_tarea ?? "Tarea sin nombre"}
                    </h3>
                    <p className="exam-subject">
                      {task.asignatura ?? "Asignatura no especificada"}
                    </p>
                    <BarChart
                      studentValue={nota}
                      classValue={media}
                      max={maxN}
                      isPending={isPending}
                      color={color}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Resumen - Añade fallbacks visuales */}
      {/* Colores brillantes para buen contraste sobre fondos oscuros */}
      {(() => {
        // Calcular si el alumno está por encima de la media en alguna categoría
        const bbAboveAverage = hasExams && !allPending && displayNota > displayMedia;
        const alexiaAboveAverage = alexiaHasAverage && alexiaStudentAverage !== null && alexiaClassAverage !== null && alexiaStudentAverage > alexiaClassAverage;
        const tareasAboveAverage = pctTareasAlumno > pctTareasClase;
        const isWinning = bbAboveAverage || alexiaAboveAverage || tareasAboveAverage;
        
        return (
          <div className={`summary ${isWinning ? 'winning' : ''}`}>
            <CelebrationEffect show={isWinning} />
            <h2 className="summary-title">
              {isWinning ? '🏆 ¡Vas Genial! 🎉' : 'Resumen Rápido'}
            </h2>
            <div className="summary-grid">
              <div className={`summary-item violet ${bbAboveAverage ? 'winner-card' : ''}`}>
                <span>Media Exámenes BlackBoard</span>
                {hasExams ? (
                  allPending ? (
                    <strong style={{ opacity: 0.7 }}>Pendiente</strong>
                  ) : (
                    <strong style={{ color: displayNota >= displayMedia ? "#4ade80" : "#f87171" }}>
                      {bbAboveAverage && <span className="trophy-icon">🏆 </span>}
                      {displayNota.toFixed(2)}/{displayMedia.toFixed(2)}
                    </strong>
                  )
                ) : (
                  <strong className="no-data">Sin exámenes</strong>
                )}
              </div>

              <div className={`summary-item purple ${alexiaAboveAverage ? 'winner-card' : ''}`}>
                <span>Media Exámenes Alexia</span>
                {alexiaHasAverage && alexiaStudentAverage !== null && alexiaClassAverage !== null ? (
                  <strong style={{ color: alexiaStudentAverage >= alexiaClassAverage ? "#4ade80" : "#f87171" }}>
                    {alexiaAboveAverage && <span className="trophy-icon">🏆 </span>}
                    {alexiaStudentAverage.toFixed(2)}/{alexiaClassAverage.toFixed(2)}
                  </strong>
                ) : (
                  <strong className="no-data">—</strong>
                )}
              </div>

              <div className={`summary-item blue ${tareasAboveAverage ? 'winner-card' : ''}`}>
                <span>Entregas Tareas (%)</span>
                <strong style={{ color: pctTareasAlumno >= pctTareasClase ? "#4ade80" : "#f87171" }}>
                  {tareasAboveAverage && <span className="trophy-icon">🏆 </span>}
                  {pctTareasAlumno.toFixed(1)}/{pctTareasClase.toFixed(1)}
                </strong>
              </div>

              <div className="summary-item indigo">
                <span>Total Tareas</span>
                <strong>{tareasTotales}</strong>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default Dashboard;